import _ from 'lodash';

const FIELD_SCHEMA_MAP: Record<string, string[]> = {
  // Preset/mode
  preset: ['preset'],

  // Identity
  widgetTitle: ['widgetTitle'],
  collapsedMessage: ['collapsedMessage'],
  inputPlaceholder: ['inputPlaceholder', 'placeholderWelcome'],
  botAvatarImageUrl: ['botAvatarImageUrl', 'botAvatarImage'],

  // Welcome strategy
  welcomeStrategy: ['welcomeStrategy'],
  defaultWelcome: ['defaultWelcome', 'defaultWelcomeMessage', 'welcomeMessage'],
  ruleBasedWelcome: ['ruleBasedWelcome', 'ruleBasedWelcomeMessages'],
  llmWelcomePrompt: ['llmWelcomePrompt'],
  llmWelcomeModel: ['llmWelcomeModel', 'llmWelcomeModelId'],

  // Behavior
  showThinkingProcess: ['showThinkingProcess', 'showThinkingProcessIndicator'],
  streamingEnabled: ['streamingEnabled'],
  'features.threadsEnabled': ['features.threadsEnabled'],
  'features.commentsEnabled': ['features.commentsEnabled'],
  'features.attachmentsEnabled': ['features.attachmentsEnabled'],
  'features.historyEnabled': ['features.historyEnabled'],
  'features.tabsEnabled': ['features.tabsEnabled'],
  'markdownSupport.enableUserMarkdown': ['markdownSupport.enableUserMarkdown'],
  'markdownSupport.enableAssistantMarkdown': ['markdownSupport.enableAssistantMarkdown'],
  'contextPrepending.url': ['contextPrepending.url', 'contextPrependingEnabled'],
  'contextPrepending.hostname': ['contextPrepending.hostname'],
  'contextPrepending.store': ['contextPrepending.store'],
  defaultMinimizedDesktop: ['defaultMinimizedDesktop', 'defaultMinimized'],
  defaultMinimizedMobile: ['defaultMinimizedMobile', 'defaultMinimized'],
  'autoOpen.delayDesktop': ['autoOpen.delayDesktop', 'autoOpenDelay'],
  'autoOpen.delayMobile': ['autoOpen.delayMobile', 'autoOpenDelay'],
  'autoOpen.oncePerSession': ['autoOpen.oncePerSession'],
  mobileMode: ['mobileMode'],
  mobileHalfHeight: ['mobileHalfHeight'],

  // Appearance (nested + legacy flattened)
  'appearance.fontFamily': ['appearance.fontFamily', 'fontFamily'],
  'appearance.fontSizeBase': ['appearance.fontSizeBase', 'fontSizeBase'],
  'appearance.widgetShadow': ['appearance.widgetShadow', 'widgetShadow'],
  'appearance.bubbleShadow': ['appearance.bubbleShadow', 'bubbleShadow'],
  'appearance.widgetMaxWidth': ['appearance.widgetMaxWidth', 'widgetMaxWidth'],
  'appearance.widgetMaxHeight': ['appearance.widgetMaxHeight', 'widgetMaxHeight'],
  'appearance.defaultMinimized': ['appearance.defaultMinimized', 'defaultMinimized'],
  'appearance.autoOpenDelay': ['appearance.autoOpenDelay', 'autoOpenDelay'],
  'appearance.showCollapsedThinking': ['appearance.showCollapsedThinking', 'showCollapsedThinking'],
  'appearance.maxMessageLength': ['appearance.maxMessageLength', 'maxMessageLength'],

  // Colors (nested + legacy flattened)
  'colors.primary': ['colors.primary', 'primaryColor', 'themeColor'],
  'colors.primaryGradient': ['colors.primaryGradient'],
  'colors.widgetBackground': ['colors.background', 'widgetBackground'],
  'colors.messagesBackground': ['colors.messagesBackground'],
  'colors.textColorPrimary': ['colors.textOnBackground', 'textColorPrimary'],
  'colors.textColorSecondary': ['colors.textColorSecondary'],
  'colors.textColorOnPrimary': ['colors.textOnPrimary', 'textColorOnPrimary'],
  'colors.userBubbleBackground': ['colors.userBubbleBackground'],
  'colors.textColorUserBubble': ['colors.textColorUserBubble'],
  'colors.botBubbleBackground': ['colors.botBubbleBackground'],
  'colors.textColorBotBubble': ['colors.textColorBotBubble'],
  'colors.botBubbleBorder': ['colors.botBubbleBorder'],
  'colors.systemBubbleBackground': ['colors.systemBubbleBackground'],
  'colors.systemBubbleColor': ['colors.systemBubbleColor'],
  'colors.systemBubbleBorder': ['colors.systemBubbleBorder'],
  'colors.borderColor': ['colors.borderColor'],
  'colors.focusRingColor': ['colors.focusRingColor'],
};

const KNOWN_API_PATHS: string[] = [
  'preset',
  'widgetTitle',
  'collapsedMessage',
  'placeholderWelcome',
  'inputPlaceholder',
  'botAvatarImageUrl',
  'botAvatarImage',
  'welcomeStrategy',
  'defaultWelcomeMessage',
  'defaultWelcome',
  'welcomeMessage',
  'ruleBasedWelcomeMessages',
  'llmWelcomePrompt',
  'llmWelcomeModelId',
  'llmWelcomeModel',
  'themeColor',
  'position',
  'contextPrependingEnabled',
  'contextPrepending.url',
  'contextPrepending.hostname',
  'contextPrepending.store',
  'showThinkingProcessIndicator',
  'showThinkingProcess',
  'streamingEnabled',
  'features.threadsEnabled',
  'features.commentsEnabled',
  'features.attachmentsEnabled',
  'features.historyEnabled',
  'features.tabsEnabled',
  'colors.primary',
  'colors.primaryGradient',
  'colors.background',
  'colors.messagesBackground',
  'colors.textOnPrimary',
  'colors.textOnBackground',
  'colors.textColorSecondary',
  'colors.userBubbleBackground',
  'colors.textColorUserBubble',
  'colors.botBubbleBackground',
  'colors.textColorBotBubble',
  'colors.botBubbleBorder',
  'colors.systemBubbleBackground',
  'colors.systemBubbleColor',
  'colors.systemBubbleBorder',
  'colors.borderColor',
  'colors.focusRingColor',
  'markdownSupport.enableUserMarkdown',
  'markdownSupport.enableAssistantMarkdown',
  'errorMessages.default',
  'errorMessages.unauthorized',
  'defaultMinimizedDesktop',
  'defaultMinimizedMobile',
  'autoOpen.delayDesktop',
  'autoOpen.delayMobile',
  'autoOpen.oncePerSession',
  'mobileMode',
  'mobileHalfHeight',
  'appearance.fontFamily',
  'appearance.fontSizeBase',
  'appearance.widgetShadow',
  'appearance.bubbleShadow',
  'appearance.widgetMaxWidth',
  'appearance.widgetMaxHeight',
  'appearance.defaultMinimized',
  'appearance.autoOpenDelay',
  'appearance.showCollapsedThinking',
  'appearance.maxMessageLength',
  // Legacy flattened appearance + color keys
  'fontFamily',
  'fontSizeBase',
  'widgetShadow',
  'bubbleShadow',
  'widgetMaxWidth',
  'widgetMaxHeight',
  'defaultMinimized',
  'autoOpenDelay',
  'showCollapsedThinking',
  'maxMessageLength',
  'primaryColor',
  'widgetBackground',
  'textColorPrimary',
  'textColorOnPrimary',
];

const KNOWN_TOP_LEVEL_SCHEMA_KEYS = new Set(
  KNOWN_API_PATHS.map((path) => path.split('.')[0]).filter(Boolean)
);
KNOWN_TOP_LEVEL_SCHEMA_KEYS.add('preset');

export const schemaHasPath = (schema: Record<string, any> | null, path: string): boolean => {
  if (!schema || typeof schema !== 'object') return true;
  const parts = path.split('.');
  let node: any = schema;
  for (const part of parts) {
    if (!node || typeof node !== 'object') return false;
    const props = node.properties || {};
    if (!props[part]) return false;
    node = props[part];
  }
  return true;
};

export const isFieldVisible = (schema: Record<string, any> | null, uiPath: string): boolean => {
  if (!schema) return true;
  const schemaPaths = FIELD_SCHEMA_MAP[uiPath] ?? [uiPath];
  return schemaPaths.some((candidate) => schemaHasPath(schema, candidate));
};

const pruneEmptyObjects = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(pruneEmptyObjects);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, val]) => [key, pruneEmptyObjects(val)] as const)
      .filter(([, val]) => {
        if (val === undefined) return false;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          return Object.keys(val).length > 0;
        }
        return true;
      });
    return Object.fromEntries(entries);
  }
  return value;
};

const isPlainObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const extractExtraConfig = (apiConfig?: Record<string, any> | null): Record<string, any> => {
  if (!apiConfig || typeof apiConfig !== 'object') return {};
  const extras = _.cloneDeep(apiConfig);
  KNOWN_API_PATHS.forEach((path) => _.unset(extras, path));
  return pruneEmptyObjects(extras) || {};
};

export const mergeConfigPayload = (base: Record<string, any>, extra?: Record<string, any>): Record<string, any> => {
  if (!extra || Object.keys(extra).length === 0) return base;
  return _.merge({}, base, extra);
};

/**
 * Runtime model: template defaults + version overrides, where overrides win.
 */
export const deepMergeForRuntime = (
  defaults?: Record<string, any> | null,
  overrides?: Record<string, any> | null
): Record<string, any> => {
  return _.mergeWith({}, defaults || {}, overrides || {}, (_objValue, srcValue) => {
    if (Array.isArray(srcValue)) {
      return _.cloneDeep(srcValue);
    }
    return undefined;
  });
};

const deepDiff = (editedValue: any, defaultValue: any): any => {
  if (_.isEqual(editedValue, defaultValue)) return undefined;

  // Array changes are stored as whole-array overrides.
  if (Array.isArray(editedValue) || Array.isArray(defaultValue)) {
    return _.cloneDeep(editedValue);
  }

  // Explicit null is a valid override value.
  if (editedValue === null) return null;

  if (!isPlainObject(editedValue) || !isPlainObject(defaultValue)) {
    return _.cloneDeep(editedValue);
  }

  const next: Record<string, any> = {};
  Object.keys(editedValue).forEach((key) => {
    const childDiff = deepDiff(editedValue[key], defaultValue[key]);
    if (childDiff !== undefined) {
      next[key] = childDiff;
    }
  });

  if (Object.keys(next).length === 0) return undefined;
  return next;
};

/**
 * Computes version config overrides against template defaults.
 */
export const computeConfigOverrides = (
  editedConfig: Record<string, any>,
  defaultConfig?: Record<string, any> | null
): Record<string, any> => {
  if (!defaultConfig || !isPlainObject(defaultConfig)) {
    return _.cloneDeep(editedConfig || {});
  }
  const diff = deepDiff(editedConfig || {}, defaultConfig);
  if (!diff || !isPlainObject(diff)) return {};
  return pruneEmptyObjects(diff);
};

export interface GenericTopLevelSchemaField {
  key: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'enum';
  enumValues?: Array<string | number | boolean>;
  label: string;
  description?: string;
}

const isSimpleSchemaProperty = (propertySchema: any): boolean => {
  if (!propertySchema || typeof propertySchema !== 'object') return false;
  if (Array.isArray(propertySchema.enum)) return true;
  const type = propertySchema.type;
  return type === 'string' || type === 'number' || type === 'integer' || type === 'boolean';
};

const resolveUiSchemaFieldNode = (uiSchema: Record<string, any> | null | undefined, key: string): Record<string, any> | null => {
  if (!uiSchema || typeof uiSchema !== 'object') return null;
  if (uiSchema[key] && typeof uiSchema[key] === 'object') return uiSchema[key];
  if (uiSchema.properties?.[key] && typeof uiSchema.properties[key] === 'object') return uiSchema.properties[key];
  return null;
};

/**
 * Returns unmapped top-level simple fields for generic rendering.
 */
export const getGenericTopLevelSchemaFields = (
  schema?: Record<string, any> | null,
  uiSchema?: Record<string, any> | null
): GenericTopLevelSchemaField[] => {
  if (!schema || typeof schema !== 'object') return [];
  const properties = schema.properties;
  if (!properties || typeof properties !== 'object') return [];

  const uiOrderRaw = Array.isArray(uiSchema?.['ui:order']) ? uiSchema?.['ui:order'] : [];
  const uiOrder = new Map<string, number>();
  uiOrderRaw.forEach((entry: unknown, index: number) => {
    if (typeof entry === 'string') uiOrder.set(entry, index);
  });

  return Object.entries(properties)
    .filter(([key, propSchema]) => !KNOWN_TOP_LEVEL_SCHEMA_KEYS.has(key) && isSimpleSchemaProperty(propSchema))
    .sort(([a], [b]) => {
      const aIndex = uiOrder.get(a);
      const bIndex = uiOrder.get(b);
      if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
      if (aIndex !== undefined) return -1;
      if (bIndex !== undefined) return 1;
      return 0;
    })
    .map(([key, propSchema]: [string, any]) => {
      const uiNode = resolveUiSchemaFieldNode(uiSchema, key);
      const type = Array.isArray(propSchema.enum) ? 'enum' : propSchema.type;
      return {
        key,
        type: (type === 'integer' ? 'integer' : type) as GenericTopLevelSchemaField['type'],
        enumValues: Array.isArray(propSchema.enum) ? propSchema.enum : undefined,
        label: uiNode?.title || propSchema?.title || key,
        description: propSchema?.description,
      };
    });
};
